import { Box, type BoxProps, Flex } from "@chakra-ui/react"
import type React from "react"

export interface ReportTableColumn {
  /** Column header text. A header row is rendered when at least one column has a header. */
  header?: React.ReactNode
  /** Text alignment for cells in this column. Defaults to "left". Right-aligned columns also get nowrap. */
  align?: "left" | "right"
  /** Fixed CSS width for this column (e.g. "40%", "200px"). When set the column will not shrink below this size. */
  width?: string
}

interface ReportTableProps extends BoxProps {
  /** Column definitions control layout, headers, and alignment. */
  columns: ReportTableColumn[]
  /** Row data — each inner array must match the columns length. */
  rows: React.ReactNode[][]
  /** Shown when rows is empty. If omitted, nothing renders for empty data. */
  emptyMessage?: React.ReactNode
}

/**
 * Low-level styled table used across the VSME report.
 *
 * Two layout modes are derived from the column count:
 *  - **2 columns** (label + value): rows use `justify="space-between"`.
 *  - **3+ columns** (e.g. breakdown tables): cells share equal width via `flex={1}`.
 *
 * Any extra `BoxProps` (including `data-*` attributes) are spread onto the
 * root element, which allows the CSS-only bold-totals trick in `GroupSection`.
 */
export const ReportTable: React.FC<ReportTableProps> = ({ columns, rows, emptyMessage, ...boxProps }) => {
  const hasHeaders = columns.some((c) => c.header)
  const equalWidths = columns.length > 2

  return (
    <Box w="100%" {...boxProps}>
      {hasHeaders && (
        <Flex
          justify="space-between"
          gap="13px"
          p="8px 4px"
          borderBottom="1px solid"
          borderColor="gray.200"
          fontSize="9px"
          fontWeight={600}
          color="gray.600"
        >
          {columns.map((col, i) => (
            <Box
              key={i}
              flex={equalWidths ? 1 : undefined}
              width={col.width}
              flexShrink={col.width ? 0 : undefined}
              textAlign={col.align ?? "left"}
            >
              {col.header}
            </Box>
          ))}
        </Flex>
      )}

      {rows.length === 0 && emptyMessage ? (
        <Flex p="12px 4px" borderBottom="1px solid" borderColor="gray.200" fontSize="10px">
          <Box color="gray.500">{emptyMessage}</Box>
        </Flex>
      ) : (
        rows.map((cells, i) => (
          <Flex
            key={i}
            justify="space-between"
            gap="13px"
            p="12px 4px"
            borderBottom="1px solid"
            borderColor="gray.200"
            fontSize="10px"
          >
            {cells.map((cell, j) => (
              <Box
                key={j}
                flex={equalWidths ? 1 : undefined}
                width={columns[j]?.width}
                flexShrink={columns[j]?.width ? 0 : undefined}
                textAlign={columns[j]?.align ?? "left"}
                whiteSpace={columns[j]?.align === "right" ? "nowrap" : undefined}
              >
                {cell}
              </Box>
            ))}
          </Flex>
        ))
      )}
    </Box>
  )
}
